/**
 * Format calendar dates without UTC-midnight off-by-one for date-only strings.
 * YYYY-MM-DD is already a timezone-aware query calendar date — parse components,
 * do not use `new Date('YYYY-MM-DD')` (UTC midnight → previous local day).
 */
import moment from 'moment-timezone'

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/

export type FormatCalendarDateOptions = {
  /** IANA zone for full timestamps (not used for date-only strings). */
  timezone?: string | null
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
  day?: 'numeric' | '2-digit'
  year?: 'numeric' | '2-digit'
}

const DEFAULT_OPTS: Required<Pick<FormatCalendarDateOptions, 'month' | 'day'>> & {
  year?: FormatCalendarDateOptions['year']
} = {
  month: 'short',
  day: 'numeric',
}

function formatLocalParts(
  year: number,
  monthIndex: number,
  day: number,
  opts: FormatCalendarDateOptions,
): string {
  const d = new Date(year, monthIndex, day)
  if (isNaN(d.getTime())) return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  const formatOpts: Intl.DateTimeFormatOptions = {
    month: opts.month ?? DEFAULT_OPTS.month,
    day: opts.day ?? DEFAULT_OPTS.day,
  }
  if (opts.year) formatOpts.year = opts.year
  return d.toLocaleDateString('en-US', formatOpts)
}

/**
 * @returns formatted date string, or original string if not parseable as a date
 */
export function formatCalendarDate(
  value: unknown,
  opts: FormatCalendarDateOptions = {},
): string {
  if (value == null || value === '') return ''

  const str = String(value).trim()
  const dateOnly = str.match(DATE_ONLY_RE)
  if (dateOnly) {
    const year = Number(dateOnly[1])
    const monthIndex = Number(dateOnly[2]) - 1
    const day = Number(dateOnly[3])
    return formatLocalParts(year, monthIndex, day, opts)
  }

  // ISO datetime starting with YYYY-MM-DD — for chart categories that are day buckets,
  // prefer the calendar date prefix so we do not shift across local midnight.
  const prefix = str.match(DATE_PREFIX_RE)
  if (prefix && (str.includes('T') || str.includes(' '))) {
    const year = Number(prefix[1])
    const monthIndex = Number(prefix[2]) - 1
    const day = Number(prefix[3])
    // If time is exactly midnight UTC and no meaningful time component for display,
    // still use calendar date from the prefix for day-level labels.
    if (/T00:00:00(\.0+)?(Z|[+-]00:00)?$/.test(str) || / 00:00:00/.test(str)) {
      return formatLocalParts(year, monthIndex, day, opts)
    }
  }

  const tz = opts.timezone && moment.tz.zone(opts.timezone) ? opts.timezone : null
  if (tz) {
    const m = moment.tz(str, tz)
    if (m.isValid()) {
      if (opts.year) return m.format('MMM D, YYYY')
      return m.format('MMM D')
    }
  }

  const parsed = new Date(str)
  if (!isNaN(parsed.getTime())) {
    const formatOpts: Intl.DateTimeFormatOptions = {
      month: opts.month ?? DEFAULT_OPTS.month,
      day: opts.day ?? DEFAULT_OPTS.day,
    }
    if (opts.year) formatOpts.year = opts.year
    return parsed.toLocaleDateString('en-US', formatOpts)
  }

  return str
}
